import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "@/shared/lib/apiClient";
import {
  fetchAdminExchangeRates,
  fetchAdminInvoice,
  updateAdminInvoice,
} from "../api/adminApi";
import { getAuthToken } from "../auth";
import type { InvoiceCurrencyCode } from "../components/InvoiceCurrencyFields";
import { isValidExchangeRateCdf } from "../components/InvoiceCurrencyFields";

export type AdminSubmitHospital = { id: number; name: string; provider_id: number };
/** id « ins:<assureur> » = facture du prestataire couvrant toutes les entreprises de cet assureur. */
export type AdminSubmitCompany = { id: number | string; name: string; company_name?: string; insurer_id?: number };

const MULTI_PREFIX = "ins:";
/** Entreprise ou assureur choisi, pour l'API. */
function companyOrInsurer(value: string): { company_id?: number; insurer_id?: number } {
  return value.startsWith(MULTI_PREFIX) ? { insurer_id: Number(value.slice(MULTI_PREFIX.length)) } : { company_id: Number(value) };
}
export type AdminSubmitConvention = {
  id: number;
  label?: string;
  type_traitement?: string;
  nature_facture?: string;
  forfait_preview?: {
    nb_familles?: number;
    nb_employes?: number;
    tarif_unitaire?: number;
    tarif_client?: number;
    effectif_scope?: string;
  };
};

export type AdminSubmitForm = {
  /** Assureur, puis prestataire de son réseau, puis entreprise (ou toutes ses entreprises). */
  insurerId: string;
  hospitalId: string;
  companyId: string;
  /** N° du lot de dépôt. */
  batchNumber: string;
  /** Date de réception de la facture : début du temps de l'Admin dans le délai convenu. */
  receivedAt: string;
  conventionId: string;
  periodFrom: string;
  periodTo: string;
  providerReference: string;
  file: File | null;
  /** Pièces complémentaires envoyées par l'assureur avec la facture détaillée. */
  fileNormale: File | null;
  fileSynthetique: File | null;
  totalNormale: string;
  totalSynthetique: string;
  invoiceCurrency: InvoiceCurrencyCode;
  exchangeRateCdf: string;
  totalAmount: string;
  rsspAmount: string;
  notes: string;
  nature: "forfait_mensuel" | "hors_forfait";
  forfaitNbFamilles: string;
  forfaitNbFamillesClient: string;
};

const initialForm: AdminSubmitForm = {
  insurerId: "",
  hospitalId: "",
  companyId: "",
  batchNumber: "",
  receivedAt: "",
  conventionId: "",
  periodFrom: "",
  periodTo: "",
  providerReference: "",
  file: null,
  fileNormale: null,
  fileSynthetique: null,
  totalNormale: "",
  totalSynthetique: "",
  invoiceCurrency: "USD",
  exchangeRateCdf: "",
  totalAmount: "",
  rsspAmount: "",
  notes: "",
  nature: "hors_forfait",
  forfaitNbFamilles: "",
  forfaitNbFamillesClient: "",
};

export function useAdminInvoiceSubmit(invoiceId?: number) {
  const isEditMode = invoiceId != null && invoiceId > 0;
  const skipDependentResetRef = useRef(false);
  const [form, setForm] = useState<AdminSubmitForm>(initialForm);
  const [hospitals, setHospitals] = useState<AdminSubmitHospital[]>([]);
  const [insurers, setInsurers] = useState<Array<{ id: number; name: string }>>([]);
  const [networkIds, setNetworkIds] = useState<number[] | null>(null);
  const [companies, setCompanies] = useState<AdminSubmitCompany[]>([]);
  const [conventions, setConventions] = useState<AdminSubmitConvention[]>([]);
  const [hospitalName, setHospitalName] = useState("Hôpital");
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingConventions, setLoadingConventions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedInvoiceId, setSubmittedInvoiceId] = useState<number | null>(null);
  const [usingApi, setUsingApi] = useState(false);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);

  const formRef = useRef(form);
  formRef.current = form;

  const updateForm = useCallback(<K extends keyof AdminSubmitForm>(key: K, value: AdminSubmitForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const selectedHospital = useMemo(
    () => hospitals.find((h) => String(h.id) === form.hospitalId) ?? null,
    [hospitals, form.hospitalId],
  );
  const selectedCompany = useMemo(
    () => companies.find((c) => String(c.id) === form.companyId) ?? null,
    [companies, form.companyId],
  );
  const selectedConvention = useMemo(
    () => conventions.find((c) => String(c.id) === form.conventionId) ?? null,
    [conventions, form.conventionId],
  );
  const isForfait = form.nature === "forfait_mensuel";

  useEffect(() => {
    if (!getAuthToken()) return;
    let cancelled = false;
    void (async () => {
      setLoadingMeta(true);
      setUsingApi(true);
      try {
        const [response, insurerRows] = await Promise.all([
          apiRequest<{ hospitals: AdminSubmitHospital[] }>("/admin/invoices/hospitals", { auth: true }),
          apiRequest<{ insurers: Array<{ id: number; name: string; status: string }> }>("/admin/insurers", { auth: true }),
        ]);
        if (!cancelled) {
          setHospitals(response.hospitals ?? []);
          setInsurers((insurerRows.insurers ?? []).filter((insurer) => insurer.status === "active"));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger les hôpitaux.");
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode || !invoiceId || !getAuthToken()) return;
    let cancelled = false;
    void (async () => {
      setLoadingEdit(true);
      setError(null);
      try {
        const invoice = await fetchAdminInvoice(invoiceId);
        if (cancelled) return;
        if (!invoice.can_edit) {
          setError("Cette facture ne peut plus être modifiée.");
          return;
        }
        skipDependentResetRef.current = true;
        setExistingFileName(invoice.file_name || null);
        const currency =
          String(invoice.invoice_currency ?? "USD").toUpperCase() === "CDF" ? "CDF" : "USD";
        setForm({
          insurerId: invoice.insurer_id ? String(invoice.insurer_id) : "",
          batchNumber: invoice.batch_number ?? "",
          receivedAt: (invoice as { received_at?: string | null }).received_at ?? "",
          hospitalId: invoice.hospital_id ? String(invoice.hospital_id) : "",
          companyId: invoice.company_id
            ? String(invoice.company_id)
            : invoice.insurer_id
              ? `${MULTI_PREFIX}${invoice.insurer_id}`
              : "",
          conventionId: invoice.convention_id ? String(invoice.convention_id) : "",
          periodFrom: invoice.period_start ?? "",
          periodTo: invoice.period_end ?? "",
          providerReference:
            invoice.provider_reference && invoice.provider_reference !== "—"
              ? invoice.provider_reference
              : "",
          file: null,
          invoiceCurrency: currency,
          exchangeRateCdf:
            invoice.exchange_rate_cdf != null && Number(invoice.exchange_rate_cdf) > 0
              ? String(invoice.exchange_rate_cdf)
              : "",
          totalAmount: String(invoice.total_amount ?? 0),
          rsspAmount: String(invoice.montant_rssp ?? 0),
          notes:
            invoice.submission_notes && invoice.submission_notes !== "—"
              ? invoice.submission_notes
              : "",
          nature: invoice.invoice_nature === "forfait_mensuel" ? "forfait_mensuel" : "hors_forfait",
          forfaitNbFamilles: "",
          forfaitNbFamillesClient: "",
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger la facture.");
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, isEditMode]);

  useEffect(() => {
    if (!form.hospitalId || !getAuthToken()) {
      setCompanies([]);
      setHospitalName("Hôpital");
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoadingCompanies(true);
      setError(null);
      try {
        const response = await apiRequest<{
          hospital_name: string;
          companies: AdminSubmitCompany[];
          insurers?: Array<{ id: number; name: string }>;
        }>(`/admin/invoices/submit-companies?hospital_id=${form.hospitalId}`, { auth: true });
        if (cancelled) return;
        setHospitalName(response.hospital_name);
        // Entreprises de l'assureur choisi dans le réseau du prestataire, ou toutes à la fois (facture multi-entreprises).
        const insurerId = Number(formRef.current.insurerId);
        const own = (response.companies ?? []).filter((company) => !insurerId || Number(company.insurer_id) === insurerId);
        setCompanies([
          ...(insurerId ? [{ id: `${MULTI_PREFIX}${insurerId}`, name: "Toutes les entreprises de l'assureur (facture multi-entreprises)" }] : []),
          ...own.map((company) => ({ id: String(company.id), name: company.company_name ?? company.name })),
        ]);
        if (skipDependentResetRef.current) {
          skipDependentResetRef.current = false;
        } else {
          setForm((current) => ({ ...current, companyId: "", conventionId: "" }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger les entreprises.");
          setCompanies([]);
        }
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.hospitalId, form.insurerId]);

  // Prestataires du réseau de l'assureur choisi.
  useEffect(() => {
    if (!form.insurerId || !getAuthToken()) {
      setNetworkIds(null);
      return;
    }
    let cancelled = false;
    void apiRequest<{ hospitals: Array<{ hospital_id: number }> }>(`/admin/insurers/${form.insurerId}/network`, { auth: true })
      .then((response) => {
        if (!cancelled) setNetworkIds((response.hospitals ?? []).map((h) => Number(h.hospital_id)));
      })
      .catch(() => {
        if (!cancelled) setNetworkIds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.insurerId]);

  const networkHospitals = useMemo(
    () => (networkIds === null ? hospitals : hospitals.filter((hospital) => networkIds.includes(Number(hospital.id)))),
    [hospitals, networkIds],
  );

  // Préremplir le taux hôpital quand devise = CDF (comme le legacy get_hospital_exchange_rate)
  useEffect(() => {
    if (!form.hospitalId || form.invoiceCurrency !== "CDF" || !getAuthToken()) return;
    if (form.exchangeRateCdf.trim() !== "") return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchAdminExchangeRates(Number(form.hospitalId));
        const row = data.hospitals.find((h) => h.hospital_id === Number(form.hospitalId));
        const rate = row?.rate_cdf_per_usd;
        if (!cancelled && rate != null && rate > 0) {
          setForm((current) =>
            current.exchangeRateCdf.trim() === ""
              ? { ...current, exchangeRateCdf: String(rate) }
              : current,
          );
        }
      } catch {
        // taux facultatif tant que l'utilisateur peut le saisir
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.hospitalId, form.invoiceCurrency, form.exchangeRateCdf]);

  useEffect(() => {
    // Pas de convention pour une facture couvrant plusieurs entreprises.
    if (!form.hospitalId || !form.companyId || form.companyId.startsWith(MULTI_PREFIX) || !getAuthToken()) {
      setConventions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoadingConventions(true);
      try {
        const params = new URLSearchParams({
          hospital_id: form.hospitalId,
          company_id: form.companyId,
        });
        if (form.periodFrom) params.set("periode_debut", form.periodFrom);
        if (form.periodTo) params.set("periode_fin", form.periodTo);
        const response = await apiRequest<{ conventions: AdminSubmitConvention[] }>(
          `/admin/invoices/submit-conventions?${params}`,
          { auth: true },
        );
        if (cancelled) return;
        const list = response.conventions ?? [];
        setConventions(list);
        setForm((current) => {
          if (
            isEditMode &&
            current.conventionId &&
            list.some((item) => String(item.id) === current.conventionId)
          ) {
            return current;
          }
          const next = { ...current };
          if (list.length === 1) {
            next.conventionId = String(list[0].id);
          } else if (
            current.conventionId &&
            !list.some((item) => String(item.id) === current.conventionId)
          ) {
            next.conventionId = "";
          }
          return next;
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger les conventions.");
          setConventions([]);
        }
      } finally {
        if (!cancelled) setLoadingConventions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.hospitalId, form.companyId, form.periodFrom, form.periodTo, isEditMode]);

  useEffect(() => {
    if (!selectedConvention?.forfait_preview || form.nature !== "forfait_mensuel") return;
    const nb = String(
      selectedConvention.forfait_preview.nb_familles ??
        selectedConvention.forfait_preview.nb_employes ??
        "",
    );
    if (!nb) return;
    setForm((current) => ({
      ...current,
      forfaitNbFamilles: current.forfaitNbFamilles || nb,
      forfaitNbFamillesClient: current.forfaitNbFamillesClient || nb,
    }));
  }, [selectedConvention, form.nature]);

  const submit = useCallback(async () => {
    if (!getAuthToken()) return;
    setSubmitting(true);
    setError(null);
    try {
      const maxBytes = 20 * 1024 * 1024;
      if (form.file && form.file.size > maxBytes) {
        throw new Error("Le fichier joint dépasse 20 Mo. Compressez le PDF puis réessayez.");
      }
      if (form.invoiceCurrency === "CDF" && !isValidExchangeRateCdf(form.exchangeRateCdf)) {
        throw new Error("Taux FC/USD invalide (entre 100 et 50 000).");
      }

      const exchangeRate =
        form.invoiceCurrency === "CDF" ? Number(form.exchangeRateCdf) : undefined;

      if (isEditMode && invoiceId) {
        const updated = await updateAdminInvoice(invoiceId, {
          hospital_id: Number(form.hospitalId) || undefined,
          ...companyOrInsurer(form.companyId),
          convention_id: form.conventionId ? Number(form.conventionId) : undefined,
          periode_debut: form.periodFrom,
          periode_fin: form.periodTo,
          nature_facture: form.nature,
          total_amount: Number(form.totalAmount) || 0,
          montant_rssp: Number(form.rsspAmount) || 0,
          reference_prestataire: form.providerReference,
          batch_number: form.batchNumber,
          received_at: form.receivedAt || undefined,
          submission_notes: form.notes,
          invoice_currency: form.invoiceCurrency,
          exchange_rate_cdf: exchangeRate,
          forfait_nb_familles: form.forfaitNbFamilles
            ? Number(form.forfaitNbFamilles)
            : undefined,
          forfait_nb_familles_client: form.forfaitNbFamillesClient
            ? Number(form.forfaitNbFamillesClient)
            : undefined,
          invoice_file: form.file ?? undefined,
        });
        setSubmittedInvoiceId(updated.id);
        return;
      }

      const body = new FormData();
      body.append("hospital_id", form.hospitalId);
      const target = companyOrInsurer(form.companyId);
      if (target.company_id) body.append("company_id", String(target.company_id));
      if (target.insurer_id) body.append("insurer_id", String(target.insurer_id));
      if (form.conventionId) body.append("convention_id", form.conventionId);
      body.append("periode_debut", form.periodFrom);
      body.append("periode_fin", form.periodTo);
      body.append("nature_facture", form.nature);
      body.append("total_amount", form.totalAmount || "0");
      body.append("montant_rssp", form.rsspAmount || "0");
      body.append("reference_prestataire", form.providerReference);
      body.append("batch_number", form.batchNumber);
      if (form.receivedAt) body.append("received_at", form.receivedAt);
      body.append("submission_notes", form.notes);
      body.append("invoice_currency", form.invoiceCurrency);
      if (exchangeRate != null) {
        body.append("exchange_rate_cdf", String(exchangeRate));
      }
      if (form.forfaitNbFamilles) {
        body.append("forfait_nb_familles", form.forfaitNbFamilles);
      }
      if (form.forfaitNbFamillesClient) {
        body.append("forfait_nb_familles_client", form.forfaitNbFamillesClient);
      }
      if (form.file) body.append("invoice_file", form.file);
      if (form.fileNormale) body.append("invoice_file_normale", form.fileNormale);
      if (form.fileSynthetique) body.append("invoice_file_synthetique", form.fileSynthetique);
      if (form.totalNormale.trim()) body.append("total_normale", form.totalNormale.trim());
      if (form.totalSynthetique.trim()) body.append("total_synthetique", form.totalSynthetique.trim());

      const response = await apiRequest<{ invoice_id: number }>(
        "/admin/invoices",
        { method: "POST", auth: true, body },
      );
      setSubmittedInvoiceId(response.invoice_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Soumission impossible.");
    } finally {
      setSubmitting(false);
    }
  }, [form, invoiceId, isEditMode]);

  return {
    form,
    updateForm,
    hospitals: networkHospitals,
    insurers,
    hospitalName,
    companies,
    conventions,
    selectedHospital,
    selectedCompany,
    selectedConvention,
    isForfait,
    isEditMode,
    existingFileName,
    loadingMeta,
    loadingEdit,
    loadingCompanies,
    loadingConventions,
    submitting,
    error,
    submittedInvoiceId,
    usingApi,
    submit,
  };
}
